import type { IModelConnection } from "@itwin/core-frontend";
import { ColorDef, GeometryStreamIterator, QueryBinder, QueryRowFormat } from "@itwin/core-common";
import type { Transform } from "@itwin/core-geometry";

import type { CurveColor, CurvePoint, IfcCurvePrimitive } from "../../domain/utilities/CurvePrimitives";
import { queryOriginForElement } from "../queries/originQuery";

type XYZ = { x: number; y: number; z: number };

type CategoryRow = {
  CategoryId?: string;
};

const ECSQL_ELEMENT_CATEGORY_3D = `
  SELECT g.Category.Id AS CategoryId
  FROM bis.GeometricElement3d g
  WHERE g.ECInstanceId = ?
`;

const ECSQL_ELEMENT_CATEGORY_2D = `
  SELECT g.Category.Id AS CategoryId
  FROM bis.GeometricElement2d g
  WHERE g.ECInstanceId = ?
`;

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function colorFromColorDef(colorDef: ColorDef, transparency: number | undefined): CurveColor {
  const c = colorDef.colors;
  const t = transparency ?? 0;
  const alpha = 1 - clamp01(t);

  const out: CurveColor = {
    r: clamp01(c.r / 255),
    g: clamp01(c.g / 255),
    b: clamp01(c.b / 255),
  };
  if (alpha < 1) out.a = clamp01(alpha);
  return out;
}

function normalizeId64Like(id: string | undefined): string {
  return (id ?? "").trim().toLowerCase();
}

function isValidId64Like(id: string | undefined): boolean {
  const n = normalizeId64Like(id);
  if (!n) return false;
  if (n === "0" || n === "0x0") return false;
  return true;
}

function id64LikeEqual(a: string | undefined, b: string | undefined): boolean {
  const na = normalizeId64Like(a);
  const nb = normalizeId64Like(b);
  if (!na || !nb) return false;
  if (na === nb) return true;

  if (na.startsWith("0x") && nb.startsWith("0x")) {
    try {
      return BigInt(na) === BigInt(nb);
    } catch {
      return false;
    }
  }

  return false;
}

async function queryElementCategoryId(
  iModel: IModelConnection,
  elementId: string,
): Promise<string | undefined> {
  const queryWithSql = async (ecsql: string): Promise<string | undefined> => {
    const reader = iModel.createQueryReader(
      ecsql,
      QueryBinder.from([elementId]),
      { rowFormat: QueryRowFormat.UseECSqlPropertyNames },
    );

    for await (const row of reader) {
      const r = (row as any).toRow?.() as CategoryRow;
      return r?.CategoryId;
    }
    return undefined;
  };

  const category3d = await queryWithSql(ECSQL_ELEMENT_CATEGORY_3D);
  if (category3d) return category3d;

  const reader = iModel.createQueryReader(
    ECSQL_ELEMENT_CATEGORY_2D,
    QueryBinder.from([elementId]),
    { rowFormat: QueryRowFormat.UseECSqlPropertyNames },
  );

  for await (const row of reader) {
    const r = (row as any).toRow?.() as CategoryRow;
    return r?.CategoryId;
  }
  return undefined;
}

async function queryCurveColorFromCategoryAppearance(
  iModel: IModelConnection,
  categoryId: string | undefined,
  categoryColorCache: Map<string, CurveColor | undefined>,
): Promise<CurveColor | undefined> {
  if (!isValidId64Like(categoryId)) return undefined;

  const cacheKey = normalizeId64Like(categoryId);
  if (categoryColorCache.has(cacheKey)) return categoryColorCache.get(cacheKey);

  try {
    const categoryInfoMap = await iModel.categories.getCategoryInfo([categoryId as string]);
    const categoryInfo = categoryInfoMap.get(categoryId as string);
    if (!categoryInfo) {
      categoryColorCache.set(cacheKey, undefined);
      return undefined;
    }

    for (const [subCategoryId, subCategory] of categoryInfo.subCategories.entries()) {
      if (!id64LikeEqual(subCategoryId, categoryId)) continue;
      if (subCategory.appearance.invisible) {
        categoryColorCache.set(cacheKey, undefined);
        return undefined;
      }
      const color = colorFromColorDef(subCategory.appearance.color, subCategory.appearance.transparency);
      categoryColorCache.set(cacheKey, color);
      return color;
    }

    categoryColorCache.set(cacheKey, undefined);
    return undefined;
  } catch {
    categoryColorCache.set(cacheKey, undefined);
    return undefined;
  }
}

async function queryCurveColorFromSubCategoryAppearance(
  iModel: IModelConnection,
  categoryId: string | undefined,
  subCategoryId: string | undefined,
  subCategoryColorCache: Map<string, CurveColor | undefined>,
  categoryColorCache: Map<string, CurveColor | undefined>,
): Promise<CurveColor | undefined> {
  if (!isValidId64Like(categoryId) || !isValidId64Like(subCategoryId)) return undefined;

  const cacheKey = `${normalizeId64Like(categoryId)}|${normalizeId64Like(subCategoryId)}`;
  if (subCategoryColorCache.has(cacheKey)) return subCategoryColorCache.get(cacheKey);

  try {
    if (id64LikeEqual(categoryId, subCategoryId)) {
      const color = await queryCurveColorFromCategoryAppearance(iModel, categoryId, categoryColorCache);
      subCategoryColorCache.set(cacheKey, color);
      return color;
    }

    const subInfoMap = await iModel.categories.getSubCategoryInfo({
      category: categoryId as string,
      subCategories: [subCategoryId as string],
    });
    const subInfo = subInfoMap.get(subCategoryId as string);
    if (!subInfo || subInfo.appearance.invisible) {
      subCategoryColorCache.set(cacheKey, undefined);
      return undefined;
    }

    const color = colorFromColorDef(subInfo.appearance.color, subInfo.appearance.transparency);
    subCategoryColorCache.set(cacheKey, color);
    return color;
  } catch {
    subCategoryColorCache.set(cacheKey, undefined);
    return undefined;
  }
}

async function resolveCurveColorFromGeomParams(
  iModel: IModelConnection,
  geomParams: any,
  categoryId: string | undefined,
  subCategoryColorCache: Map<string, CurveColor | undefined>,
  categoryColorCache: Map<string, CurveColor | undefined>,
): Promise<CurveColor | undefined> {
  const lineColor = geomParams?.lineColor;
  if (lineColor && typeof lineColor === "object") {
    try {
      const transparency =
        typeof geomParams?.elmTransparency === "number"
          ? geomParams.elmTransparency
          : undefined;
      return colorFromColorDef(lineColor as ColorDef, transparency);
    } catch {
      // ignore
    }
  }

  const subCategoryId = typeof geomParams?.subCategoryId === "string" ? geomParams.subCategoryId : undefined;
  if (categoryId && subCategoryId) {
    const subCategoryColor = await queryCurveColorFromSubCategoryAppearance(
      iModel,
      categoryId,
      subCategoryId,
      subCategoryColorCache,
      categoryColorCache,
    );
    if (subCategoryColor) return subCategoryColor;
  }

  const fillColor = geomParams?.fillColor;
  if (fillColor && typeof fillColor === "object") {
    try {
      const transparency =
        typeof geomParams?.fillTransparency === "number"
          ? geomParams.fillTransparency
          : typeof geomParams?.elmTransparency === "number"
            ? geomParams.elmTransparency
            : undefined;
      return colorFromColorDef(fillColor as ColorDef, transparency);
    } catch {
      // ignore
    }
  }

  return queryCurveColorFromCategoryAppearance(iModel, categoryId, categoryColorCache);
}

function toPointTuple(
  p: XYZ,
  origin: XYZ,
  makeLocal: boolean,
  unitScale: number
): CurvePoint {
  const x = (makeLocal ? p.x - origin.x : p.x) * unitScale;
  const y = (makeLocal ? p.y - origin.y : p.y) * unitScale;
  const z = (makeLocal ? p.z - origin.z : p.z) * unitScale;
  return [x, y, z];
}

function vecToTuple(v: XYZ, unitScale: number): CurvePoint {
  return [v.x * unitScale, v.y * unitScale, v.z * unitScale];
}

function getArcFlags(curve: any): { isCircle: boolean; isFullCircle: boolean } {
  const radius0 = Math.hypot(curve?.vector0?.x ?? 0, curve?.vector0?.y ?? 0, curve?.vector0?.z ?? 0);
  const radius90 = Math.hypot(
    curve?.vector90?.x ?? 0,
    curve?.vector90?.y ?? 0,
    curve?.vector90?.z ?? 0
  );

  const isCircle = Number.isFinite(radius0) && Number.isFinite(radius90) && Math.abs(radius0 - radius90) < 1e-6;
  const startDeg = Number(curve?.sweep?.startDegrees ?? 0);
  const endDeg = Number(curve?.sweep?.endDegrees ?? 0);
  const spanDeg = Math.abs(endDeg - startDeg);
  const isFullCircle = Math.abs(spanDeg - 360) < 1e-6;

  return { isCircle, isFullCircle };
}

function extractFromCurvePrimitive(
  cp: any,
  origin: XYZ,
  makeLocal: boolean,
  unitScale: number,
  color: CurveColor | undefined
): IfcCurvePrimitive[] {
  const type = cp?.curvePrimitiveType as string | undefined;
  if (!type) return [];

  if (type === "lineSegment") {
    const p0 = cp.point0Ref ?? cp.startPoint?.();
    const p1 = cp.point1Ref ?? cp.endPoint?.();
    if (!p0 || !p1) return [];
    return [
      {
        kind: "line",
        start: toPointTuple(p0, origin, makeLocal, unitScale),
        end: toPointTuple(p1, origin, makeLocal, unitScale),
        color,
      },
    ];
  }

  if (type === "lineString") {
    const n = Number(cp.numPoints?.() ?? 0);
    if (!Number.isFinite(n) || n < 2) return [];
    const points: CurvePoint[] = [];
    for (let i = 0; i < n; i++) {
      const p = cp.pointAt?.(i);
      if (!p) continue;
      points.push(toPointTuple(p, origin, makeLocal, unitScale));
    }
    if (points.length < 2) return [];
    return [{ kind: "polyline", points, color }];
  }

  if (type === "arc") {
    const vectors = cp.toVectors?.();
    if (!vectors?.center || !vectors?.vector0 || !vectors?.vector90) return [];

    const start = cp.startPoint?.();
    const end = cp.endPoint?.();
    if (!start || !end) return [];

    const { isCircle, isFullCircle } = getArcFlags(vectors);

    return [
      {
        kind: "arc",
        center: toPointTuple(vectors.center, origin, makeLocal, unitScale),
        vector0: vecToTuple(vectors.vector0, unitScale),
        vector90: vecToTuple(vectors.vector90, unitScale),
        start: toPointTuple(start, origin, makeLocal, unitScale),
        end: toPointTuple(end, origin, makeLocal, unitScale),
        isCircle,
        isFullCircle,
        color,
      },
    ];
  }

  if (typeof cp.collectCurvePrimitives === "function") {
    const lowerType = type.toLowerCase();
    // iTwin conduit centerlines often come through as complex/compound chain-like
    // primitives. Flatten them recursively into supported leaf primitives.
    if (lowerType.includes("chain") || lowerType === "path" || lowerType === "loop") {
      const nested = cp.collectCurvePrimitives() as any[];
      const out: IfcCurvePrimitive[] = [];
      for (const child of nested) {
        out.push(...extractFromCurvePrimitive(child, origin, makeLocal, unitScale, color));
      }
      return out;
    }
  }

  return [];
}

function extractFromGeometryQuery(
  geometry: any,
  origin: XYZ,
  makeLocal: boolean,
  unitScale: number,
  color: CurveColor | undefined
): IfcCurvePrimitive[] {
  if (!geometry) return [];

  if (typeof geometry.curvePrimitiveType === "string") {
    return extractFromCurvePrimitive(geometry, origin, makeLocal, unitScale, color);
  }

  if (typeof geometry.collectCurvePrimitives === "function") {
    const cps = geometry.collectCurvePrimitives() as any[];
    const out: IfcCurvePrimitive[] = [];
    for (const cp of cps) {
      out.push(...extractFromCurvePrimitive(cp, origin, makeLocal, unitScale, color));
    }
    return out;
  }

  return [];
}

function makeIteratorForElement(props: any): GeometryStreamIterator | undefined {
  if (!props?.geom) return undefined;

  try {
    return GeometryStreamIterator.fromGeometricElement3d(props);
  } catch {
    // ignore and try 2d
  }

  try {
    return GeometryStreamIterator.fromGeometricElement2d(props);
  } catch {
    // ignore and fallback
  }

  try {
    return new GeometryStreamIterator(props.geom, props.category);
  } catch {
    return undefined;
  }
}

async function collectCurvesFromPart(
  iModel: IModelConnection,
  partId: string,
  origin: XYZ,
  makeLocal: boolean,
  unitScale: number,
  partToWorld: Transform | undefined,
  partStack: Set<string>,
  partCache: Map<string, any>,
  categoryId: string | undefined,
  inheritedColor: CurveColor | undefined,
  inheritedGeomParams: any,
  subCategoryColorCache: Map<string, CurveColor | undefined>,
  categoryColorCache: Map<string, CurveColor | undefined>,
): Promise<IfcCurvePrimitive[]> {
  // Geometry parts can reference other parts recursively. Guard cycles to avoid
  // pathological recursion in malformed or highly-shared part graphs.
  if (partStack.has(partId)) return [];
  partStack.add(partId);

  try {
    let partProps = partCache.get(partId);
    if (!partProps) {
      partProps = await iModel.elements.loadProps(partId, { wantGeometry: true });
      if (!partProps) return [];
      partCache.set(partId, partProps);
    }

    if (!partProps.geom) return [];

    let iter: GeometryStreamIterator;
    try {
      iter = GeometryStreamIterator.fromGeometryPart(partProps, inheritedGeomParams, partToWorld);
    } catch {
      iter = GeometryStreamIterator.fromGeometryPart(partProps, undefined, partToWorld);
    }

    const out: IfcCurvePrimitive[] = [];

    for (const entry of iter) {
      const primitive = entry.primitive as any;
      // Prefer per-entry symbology, but inherit color from the parent part reference
      // when the nested entry does not carry its own display params.
      const entryColor =
        (await resolveCurveColorFromGeomParams(
          iModel,
          (entry as any).geomParams,
          categoryId,
          subCategoryColorCache,
          categoryColorCache,
        )) ?? inheritedColor;

      if (primitive?.type === "geometryQuery") {
        out.push(...extractFromGeometryQuery(primitive.geometry, origin, makeLocal, unitScale, entryColor));
        continue;
      }

      if (primitive?.type === "partReference" && primitive.part?.id) {
        const nestedToLocal = primitive.part.toLocal as Transform | undefined;
        // Compose transforms as we descend so extracted curve points remain in the
        // element's frame before the IFC local placement is applied.
        const nestedTransform =
          partToWorld && nestedToLocal
            ? partToWorld.multiplyTransformTransform(nestedToLocal)
            : partToWorld ?? nestedToLocal;

        const nested = await collectCurvesFromPart(
          iModel,
          primitive.part.id,
          origin,
          makeLocal,
          unitScale,
          nestedTransform,
          partStack,
          partCache,
          categoryId,
          entryColor,
          (entry as any).geomParams,
          subCategoryColorCache,
          categoryColorCache,
        );
        out.push(...nested);
      }
    }

    return out;
  } finally {
    partStack.delete(partId);
  }
}

export async function extractIfcCurvesForElement(
  iModel: IModelConnection,
  elementId: string,
  opts?: {
    makeLocal?: boolean;
    unitScale?: number;
  }
): Promise<IfcCurvePrimitive[]> {
  const makeLocal = opts?.makeLocal ?? true;
  const unitScale = opts?.unitScale ?? 1.0;

  const originObj = await queryOriginForElement(iModel, elementId);
  const origin: XYZ = {
    x: Number(originObj?.x ?? 0),
    y: Number(originObj?.y ?? 0),
    z: Number(originObj?.z ?? 0),
  };

  const elementCategoryId = await queryElementCategoryId(iModel, elementId);

  const props = (await iModel.elements.loadProps(elementId, { wantGeometry: true })) as any;
  if (!props?.geom) return [];

  const iter = makeIteratorForElement(props);
  if (!iter) return [];

  const out: IfcCurvePrimitive[] = [];
  const partCache = new Map<string, any>();
  const subCategoryColorCache = new Map<string, CurveColor | undefined>();
  const categoryColorCache = new Map<string, CurveColor | undefined>();

  for (const entry of iter) {
    const primitive = entry.primitive as any;
    const entryColor = await resolveCurveColorFromGeomParams(
      iModel,
      (entry as any).geomParams,
      elementCategoryId,
      subCategoryColorCache,
      categoryColorCache,
    );

    if (primitive?.type === "geometryQuery") {
      out.push(...extractFromGeometryQuery(primitive.geometry, origin, makeLocal, unitScale, entryColor));
      continue;
    }

    if (primitive?.type === "partReference" && primitive.part?.id) {
      const curves = await collectCurvesFromPart(
        iModel,
        primitive.part.id,
        origin,
        makeLocal,
        unitScale,
        primitive.part.toLocal as Transform | undefined,
        new Set<string>(),
        partCache,
        elementCategoryId,
        entryColor,
        (entry as any).geomParams,
        subCategoryColorCache,
        categoryColorCache,
      );
      out.push(...curves);
    }
  }

  return out;
}
